import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import classnames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import { FaTrashAlt, FaUndo, FaCalendarAlt, FaClipboardList } from 'react-icons/fa';

import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import FormattedDayMonth from '@/shared/components/FormattedDayMonth';
import EditableDayMonthPicker from '@/shared/components/editable/EditableDayMonthPicker';
import nullable from '@/shared/utils/nullable';
import Button from '@/shared/components/Button';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import EditableSelect from '../../../shared/components/editable/EditableSelect';
import messages from './invoiceScheduleMessages';

import api from '../../../shared/api';
import config from '../../../config';
import ToastrService from '../../../shared/services/ToastrService';
import handleHttpError from '../../../shared/services/HandleHttpError';

import TranslateListItem from '../../../shared/components/translations/TranslateListItem';
import translationHelper from '../../../shared/components/translations/TranslationHelper';
import guid from '@/shared/utils/guid';
import HasFeature from '@/shared/components/security/HasFeature';
import authService, { features } from '@/shared/services/AuthService';
import Checkbox from '@/shared/components/input/Checkbox';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import EditableNumber from '@/shared/components/editable/EditableNumber';
import { getDeviationDays } from '@/shared/services/ObjectDisplayService';
import dateFormatService from '@/shared/services/DateFormatService';
import SimpleTooltip from '@/shared/components/SimpleTooltip';
import { isNumberInRange } from '@/shared/utils/validationHelpers';

const lineSort = dynamicSortMultiple('date', 'isFixed');
const getInvoiceTextType = (invoicingType, fixed) => {
  switch (invoicingType) {
    case 0:
      return 3;
    case 1:
      return 0;
    case 2:
      return fixed ? 1 : 2;
    default:
      return -1;
  }
};
const getDisplayValueForInvoiceText = (line, data, invoiceTexts, intl) => {
  if (line.invoiceTextId) return undefined;
  const invoiceTextId =
    data.invoicingType === 2 && !line.fixed ? data.settlmentInvoiceTextId : data.invoiceTextId;

  if (!invoiceTextId) return undefined;
  const text = invoiceTexts.find(x => x.id === invoiceTextId);
  if (!text) return undefined;
  return (
    <span className="italic">
      {intl.formatMessage(listItemMsg('invoicetext', text.id, text.name))}
    </span>
  );
};

export const calcEndOfMonth = date =>
  moment
    .utc(date)
    .startOf('month')
    .add(1, 'month')
    .add(-1, 'day');

class InvoiceScheduleModal extends Component {
  state = {
    data: null,
    saving: false,
    showValidation: false,
    taskTemplates: [],
    fullScreen: false,
  };

  componentDidUpdate(prevProps) {
    const { schedule: newSchedule } = this.props;

    if (JSON.stringify(prevProps.schedule) !== JSON.stringify(newSchedule)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        saving: false,
        data: newSchedule
          ? {
              invoicingType: 0,
              name: '',
              inUse: false,
              useReferenceYear: false,
              ...newSchedule,
              lines: (newSchedule.lines || []).sort(lineSort),
            }
          : null,
        translations: null,
        showValidation: false,
      });
      if (newSchedule && !newSchedule.id) {
        translationHelper.create('invoiceSchedule').then(translations => {
          this.setState({ translations });
        });
      }
    }
  }
  retrieveTaskTemplates = () => {
    const { intl } = this.props;

    return api.base.get(`${config.appUrls.tasks}/api/TaskTemplates/Templates`).then(({ data }) => {
      const translatedTemplates = data
        .map(t => ({
          ...t,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)),
          children: (t.children || [])
            .map(s => ({
              ...s,
              label: intl.formatMessage(listItemMsg('taskTemplate', s.id, s.name)),
            }))
            .sort(dynamicSort('priority')),
        }))
        .filter(t => t.children.length > 0)
        .sort(dynamicSort('label'));

      this.setState({
        taskTemplates: translatedTemplates,
      });
    });
  };

  componentDidMount() {
    this.hasTaskflowBilling = authService.hasFeature(features.TaskflowBilling);
    this.hasReferenceYear = authService.hasFeature(features.ReferenceYear);
    if (this.hasTaskflowBilling) {
      this.retrieveTaskTemplates();
    }
  }

  updateData = value => {
    this.setState(state => ({
      data: { ...state.data, ...value },
    }));
  };

  calcNextDate = prop => {
    const { data } = this.state;
    if (!data || !data.lines || !data.lines.length) return null;
    if (data.lines.length === 1)
      return moment
        .utc(data.lines[0][prop])
        .add(1, 'month')
        .year(2000);

    const line1 = moment.utc(data.lines[data.lines.length - 2][prop]);
    const line2 = moment.utc(data.lines[data.lines.length - 1][prop]);

    if (line1.isAfter(line2)) {
      line1.add(-1, 'year');
    }

    const line1Eom = calcEndOfMonth(line1);
    const line2Eom = calcEndOfMonth(line2);

    const diffMonths = line2
      .clone()
      .startOf('month')
      .diff(line1.clone().startOf('month'), 'month');

    if (line1.isSame(line1Eom) && line2.isSame(line2Eom)) {
      return calcEndOfMonth(line2.clone().add(diffMonths, 'month')).year(2000);
    }

    if (line1.date() === line2.date()) {
      return line2
        .clone()
        .add(diffMonths, 'month')
        .year(2000);
    }

    const eomDiff = line2.diff(line2Eom, 'days');
    if (line1.diff(line1Eom, 'days') === eomDiff) {
      return calcEndOfMonth(
        line2
          .clone()
          .startOf('month')
          .add(diffMonths, 'month'),
      )
        .add(eomDiff, 'days')
        .year(2000);
    }

    const diffDays = line2.diff(line1, 'days');
    return line2
      .clone()
      .add(diffDays, 'days')
      .year(2000);
  };

  addLine = line => {
    const id = Math.random()
      .toString()
      .replace('0.', '');
    const {
      data: { invoicingType },
    } = this.state;

    const nextInvoiceDate =
      this.calcNextDate('invoiceCreationDate') || moment.utc({ year: 2000, month: 0, day: 1 });

    this.setState(state => {
      const [lastLine] = state.data.lines?.slice(-1);

      return {
        data: {
          ...state.data,
          lines: [
            ...state.data.lines,
            {
              id,
              isNew: true,
              registrationsUntil:
                invoicingType === 2 && line.fixed
                  ? nextInvoiceDate
                      .clone()
                      .add(-1, 'days')
                      .year(2000)
                  : this.calcNextDate('registrationsUntil') ||
                    moment.utc({ year: 2000, month: 11, day: 31 }),
              invoiceCreationDate: nextInvoiceDate,
              invoiceTextId: null,
              mainTaskTemplateId: lastLine?.mainTaskTemplateId,
              subtaskTemplateId: lastLine?.subtaskTemplateId,
              referenceYearDeviation: lastLine?.referenceYearDeviation ?? 0,
              ...line,
            },
          ].sort(lineSort),
        },
      };
    });
  };

  updateLine = (line, value, recursive = false) => {
    const errors = this.validateLine({ ...line, ...value });
    const { intl } = this.props;
    if (!errors.isValid) {
      if (
        errors.registrationsUntil ===
        'registrationsUntil must be lower than the invoiceCreationDate'
      ) {
        if (
          !recursive &&
          value.registrationsUntil &&
          value.registrationsUntil !== line.registrationsUntil
        ) {
          ToastrService.info(
            intl.formatMessage(messages.InvoiceScheduleChangedInvoiceCreationDateWarning),
          );

          return this.updateLine(
            line,
            {
              ...value,
              invoiceCreationDate: value.registrationsUntil
                .clone()
                .add(1, 'days')
                .year(2000),
            },
            true,
          );
        }
        if (
          !recursive &&
          value.invoiceCreationDate &&
          value.invoiceCreationDate !== line.invoiceCreationDate
        ) {
          ToastrService.info(
            intl.formatMessage(messages.InvoiceScheduleChangedRegistrationsUntilWarning),
          );
          return this.updateLine(
            line,
            {
              ...value,
              registrationsUntil: value.invoiceCreationDate
                .clone()
                .add(-1, 'days')
                .year(2000),
            },
            true,
          );
        }
        ToastrService.error(
          intl.formatMessage({
            id: `exceptioncode_2052`,
            defaultMessage: errors.registrationsUntil,
          }),
        );
        return Promise.reject();
      }
      //return Promise.reject();
    }

    this.setState(state => ({
      data: {
        ...state.data,
        lines: state.data.lines
          .map(l => {
            if (l.id === line.id) {
              if (value.invoiceCreationDate && state.data.invoicingType === 2 && l.fixed) {
                return {
                  ...l,
                  ...value,
                  registrationsUntil: value.invoiceCreationDate
                    .clone()
                    .add(-1, 'days')
                    .year(2000),
                };
              }
              return { ...l, ...value };
            }
            return l;
          })
          .sort(lineSort),
      },
    }));
  };
  removeLine = line => {
    if (line.isNew) {
      this.setState(state => ({
        data: {
          ...state.data,
          lines: state.data.lines.filter(l => l.id !== line.id),
        },
      }));
    } else {
      this.updateLine(line, { isRemoved: true });
    }
  };
  save = () => {
    const { onClose, intl } = this.props;
    const { saving, data, translations } = this.state;

    if (saving || !data || !this.validate().isValid) return Promise.reject();

    const endpoint = `${config.appUrls.api}/api/InvoiceSchedule`;
    const action = data.id ? api.base.put : api.base.post;

    const model = {
      ...data,
      lines: data.lines.map(
        ({ id, isNew, referenceYearDeviation, mainTaskTemplateId, ...rest }) => {
          const mapped = {
            mainTaskTemplateId,
            referenceYearDeviation:
              !data.useReferenceYear || !!mainTaskTemplateId ? 0 : referenceYearDeviation,
            ...rest,
          };
          // only bind id if it's not null, otherwise our API endpoint cant bind the model..
          if (!isNew) {
            mapped.id = id;
          }
          return mapped;
        },
      ),
    };

    return new Promise((resolve, reject) => {
      this.setState({ saving: true }, () => {
        action(endpoint, model)
          .then(({ data: result }) => {
            if (!model.id && result.id) {
              // Create request is finished, save translations before handling the result
              return translationHelper
                .save(translationHelper.setKey(translations, 'invoiceSchedule', result.id))
                .then(() => result); // Return the original result (not the translation result)
            }
            // No need to save translations, pass result to next handler
            return result;
          })
          .then(result => {
            if (model.inUse) {
              ToastrService.info(intl.formatMessage(messages.InvoiceScheduleChangesQueued));
            } else {
              ToastrService.changesSaved(intl);
            }

            this.setState({
              saving: false,
            });
            onClose(result);
            resolve();
          })
          .catch(handleHttpError(intl))
          .catch(error => {
            this.setState({
              saving: false,
            });
            reject(error);
          });
      });
    });
  };
  validate = () => {
    const { data, translations } = this.state;
    const result = {
      isValid: true,
      lines: {},
    };
    if (!data) return result;
    if (!data.name) {
      result.name = 'Name is required';
      result.isValid = false;
    }
    if (translations && !translationHelper.validate(translations)) {
      result.isValid = false;
      result.translations = 'Translations validation error';
    }

    if (!data.lines || !data.lines.length) {
      result.isValid = false;
    } else {
      data.lines.forEach(line => {
        const { isValid, ...lineErrors } = this.validateLine(line);
        if (!isValid) {
          result.lines[line.id] = lineErrors;
          result.isValid = false;
        }
      });
    }

    return result;
  };

  validateLine = line => {
    const { data } = this.state;
    let result = { isValid: true };
    if (line.mainTaskTemplateId) {
      if (line.mainTaskTemplateId === guid.empty) {
        result = {
          ...result,
          isValid: false,
          mainTaskTemplateId: 'MainTask is required',
        };
      }
      if (!line.subtaskTemplateId || line.subtaskTemplateId === guid.empty) {
        result = {
          ...result,
          isValid: false,
          subtaskTemplateId: 'Subtask is required',
        };
      }
    } else {
      if (!line.invoiceCreationDate) {
        result = {
          ...result,
          isValid: false,
          invoiceCreationDate: 'invoiceCreationDate is required',
        };
      }
      if (!line.registrationsUntil) {
        result = {
          ...result,
          isValid: false,
          registrationsUntil: 'registrationsUntil is required',
        };
      }
      if (
        line.invoiceCreationDate &&
        line.registrationsUntil &&
        line.invoiceCreationDate.isBefore(line.registrationsUntil) &&
        moment
          .utc(line.registrationsUntil)
          .add(-6, 'months')
          .isBefore(line.invoiceCreationDate)
      ) {
        result = {
          ...result,
          isValid: false,
          registrationsUntil: 'registrationsUntil must be lower than the invoiceCreationDate',
        };
      }
      if (
        this.hasReferenceYear &&
        data.useReferenceYear &&
        !isNumberInRange(line.referenceYearDeviation, -366, 366)
      ) {
        result = {
          ...result,
          isValid: false,
          referenceYearDeviation: 'referenceYearDeviation must be between -366 and 366',
        };
      }
    }
    return result;
  };

  getSubTasks = mainTaskTemplateId => {
    const { taskTemplates } = this.state;
    if (!mainTaskTemplateId || mainTaskTemplateId === guid.empty || !taskTemplates) return [];
    const taskTemplate = taskTemplates.find(x => x.id === mainTaskTemplateId);
    return taskTemplate?.children ?? [];
  };

  showValidation = () => {
    const { showValidation } = this.state;
    if (!showValidation) this.setState({ showValidation: true });
  };

  updateTranslations = translations => this.setState({ translations });
  changeName = name => this.updateData({ name });

  render() {
    const { onClose, intl, invoiceTexts } = this.props;
    const { data, saving, showValidation, translations, taskTemplates, fullScreen } = this.state;
    const show = !!data;
    const isNew = !data || !data.id;
    const errors = this.validate();
    if (!errors.isValid && process.env.NODE_ENV !== 'production') console.warn(errors);

    const invoiceTextOptions =
      invoiceTexts &&
      invoiceTexts.reduce(
        (dic, t) => {
          if (!dic[t.type]) dic[t.type] = []; // eslint-disable-line no-param-reassign
          dic[t.type].push({
            id: t.id,
            label: intl.formatMessage(listItemMsg('invoicetext', t.id, t.name)),
          });
          return dic;
        },
        { 0: [], 1: [], 2: [], 3: [] },
      );

    const [normalLines, taskflowLines] = (data?.lines || []).reduce(
      (a, x) => {
        a[x.mainTaskTemplateId ? 1 : 0].push(x);
        return a;
      },
      [[], []],
    );

    return (
      <Modal
        backdrop="static"
        show={show}
        onHide={() => onClose(null)}
        keyboard={false}
        dialogClassName={classnames('modal-lg', {
          'full-screen': fullScreen,
        })}
      >
        <Modal.Header closeButton>
          <button
            type="button"
            className="toggle-full-screen"
            onClick={() => this.setState(state => ({ fullScreen: !state.fullScreen }))}
          >
            <i
              className={classnames('fa', {
                'fa-compress': fullScreen,
                'fa-expand': !fullScreen,
              })}
            />
          </button>
          <Modal.Title>
            {isNew ? (
              <FormattedMessage {...messages.CreateInvoiceSchedule} />
            ) : (
              <FormattedMessage {...messages.EditInvoiceSchedule} />
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {show && (
            <div className="form-horizontal">
              {translations && isNew && (
                <TranslateListItem
                  translations={translations}
                  label={intl.formatMessage(messages.Name)}
                  labelClass="col-sm-4"
                  inputClass="col-sm-8"
                  onChange={this.updateTranslations}
                  onDefaultChanged={this.changeName}
                  showValidation={showValidation}
                  disabled={saving}
                  maxLength={255}
                />
              )}
              {!isNew && (
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.Name} />
                  </label>
                  <div className="col-sm-8">
                    <div className="form-control-static">
                      <FormattedListValue
                        list="invoiceSchedule"
                        id={data.id}
                        fallback={data.name}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.InvoiceScheduleType} />
                </label>
                <div className="col-sm-8">
                  {isNew && data.lines.length === 0 ? (
                    <select
                      className="form-control"
                      required
                      value={data.invoicingType}
                      onChange={e =>
                        this.updateData({ invoicingType: parseInt(e.target.value, 10) })
                      }
                    >
                      <option value={0}>
                        {intl.formatMessage(listItemMsg('invoicingtype', '0'))}
                      </option>
                      <option value={1}>
                        {intl.formatMessage(listItemMsg('invoicingtype', '1'))}
                      </option>
                      <option value={2}>
                        {intl.formatMessage(listItemMsg('invoicingtype', '2'))}
                      </option>
                    </select>
                  ) : (
                    <div className="form-control-static">
                      <FormattedListValue list="invoicingtype" id={data.invoicingType} />
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.InvoiceText} />
                </label>
                <div className="col-sm-8">
                  <select
                    className="form-control"
                    value={data.invoiceTextId}
                    onChange={e => this.updateData({ invoiceTextId: e.target.value || null })}
                  >
                    <option />

                    {(invoiceTextOptions[getInvoiceTextType(data.invoicingType, true)] || []).map(
                      t => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>
              {data.invoicingType === 2 ? (
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.InvoiceTextForSettlementInvoices} />
                  </label>
                  <div className="col-sm-8">
                    <select
                      className="form-control"
                      value={data.settlementInvoiceTextId}
                      onChange={e =>
                        this.updateData({ settlementInvoiceTextId: e.target.value || null })
                      }
                    >
                      <option />
                      {(invoiceTextOptions[2] || []).map(t => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}
              {data.invoicingType === 1 || data.invoicingType === 2 ? (
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    {data.invoicingType === 1 && (
                      <FormattedMessage {...messages.InvoiceTextForExForfaitRegistrationInvoices} />
                    )}
                    {data.invoicingType === 2 && (
                      <FormattedMessage
                        {...messages.InvoiceTextForNotInAdvanceRegistrationInvoices}
                      />
                    )}
                  </label>
                  <div className="col-sm-8">
                    <select
                      className="form-control"
                      value={data.separateInvoiceTextId}
                      onChange={e =>
                        this.updateData({ separateInvoiceTextId: e.target.value || null })
                      }
                    >
                      <option />
                      <optgroup label={intl.formatMessage(listItemMsg('invoiceTextType', 3))}>
                        {invoiceTextOptions[3].map(t => (
                          <option key={t.id} value={t.id}>
                            {t.label}
                          </option>
                        ))}
                      </optgroup>
                      {data.invoicingType === 2 ? (
                        <optgroup label={intl.formatMessage(listItemMsg('invoiceTextType', 2))}>
                          {invoiceTextOptions[2].map(t => (
                            <option key={t.id} value={t.id}>
                              {t.label}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                    </select>
                  </div>
                </div>
              ) : null}
              {this.hasReferenceYear ? (
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.BasedOnReferenceYear} />
                    <ZendeskHelp article="23291843922716" />
                  </label>
                  <div className="col-sm-8">
                    <Checkbox
                      value={data.useReferenceYear}
                      onClick={value => this.updateData({ useReferenceYear: !!value })}
                      disabled={saving}
                    />
                  </div>
                </div>
              ) : null}

              {!isNew && data.inUse && (
                <div className="alert alert-warning">
                  <FormattedMessage {...messages.InvoiceScheduleInUseWarning} />
                </div>
              )}
              <hr />

              {normalLines.length > 0 && (
                <table className="table table-hover table-layout">
                  <thead>
                    <tr>
                      {this.hasTaskflowBilling ? (
                        <th className="text-center">
                          <FormattedMessage {...messages.IsTaskflowBilling} />
                          <ZendeskHelp article="19193674748188" />
                        </th>
                      ) : null}
                      <th>
                        <FormattedMessage {...messages.RegistrationsUntil} />
                      </th>
                      <th>
                        <FormattedMessage {...messages.InvoiceCreationDate} />
                      </th>

                      {data.invoicingType === 2 && (
                        <th>
                          <FormattedMessage {...messages.AdvanceOrSettlement} />
                        </th>
                      )}
                      <th>
                        <FormattedMessage {...messages.InvoiceText} />
                      </th>
                      {this.hasReferenceYear && data.useReferenceYear ? (
                        <th>
                          <span title={intl.formatMessage(messages.FiscalYearOfRegistrationUntil)}>
                            <FormattedMessage {...messages.ReferenceYear} />
                          </span>
                        </th>
                      ) : null}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {normalLines.map(line => (
                      <tr key={line.id} className={classnames({ 'mark-removed': line.isRemoved })}>
                        {this.hasTaskflowBilling ? (
                          <td className="text-center col-icons">
                            <Checkbox
                              value={false}
                              onClick={() =>
                                this.updateLine(line, {
                                  mainTaskTemplateId: guid.empty,
                                  subtaskTemplateId: null,
                                })
                              }
                              disabled={saving}
                            />
                          </td>
                        ) : null}
                        <td
                          className={classnames('col-md', {
                            markedError:
                              errors.lines[line.id] && errors.lines[line.id].registrationsUntil,
                          })}
                        >
                          {!line.mainTaskTemplateId && (
                            <EditableDayMonthPicker
                              value={line.registrationsUntil}
                              outputType="moment"
                              required
                              onChange={date => this.updateLine(line, { registrationsUntil: date })}
                              displayValue={
                                !!line.registrationsUntil && (
                                  <FormattedDayMonth value={line.registrationsUntil} />
                                )
                              }
                            />
                          )}
                        </td>
                        <td
                          className={classnames('col-md', {
                            markedError:
                              errors.lines[line.id] && errors.lines[line.id].invoiceCreationDate,
                          })}
                        >
                          {!line.mainTaskTemplateId && (
                            <EditableDayMonthPicker
                              value={line.invoiceCreationDate}
                              outputType="moment"
                              onChange={date =>
                                this.updateLine(line, { invoiceCreationDate: date })
                              }
                              required
                              displayValue={
                                !!line.invoiceCreationDate && (
                                  <FormattedDayMonth value={line.invoiceCreationDate} />
                                )
                              }
                            />
                          )}
                        </td>
                        {data.invoicingType === 2 && (
                          <td className="col-sm">
                            {line.fixed ? (
                              <FormattedMessage {...messages.Advance} />
                            ) : (
                              <FormattedMessage {...messages.Settlement} />
                            )}
                          </td>
                        )}

                        <td className="col-md">
                          <EditableSelect
                            value={line.invoiceTextId}
                            onChange={invoiceTextId => this.updateLine(line, { invoiceTextId })}
                            options={
                              invoiceTextOptions[
                                getInvoiceTextType(data.invoicingType, line.fixed)
                              ] || []
                            }
                            bindToId
                            displayValue={getDisplayValueForInvoiceText(
                              line,
                              data,
                              invoiceTexts,
                              intl,
                            )}
                          />
                        </td>
                        {this.hasReferenceYear && data.useReferenceYear ? (
                          <td
                            className={classnames('col-md', {
                              markedError:
                                errors.lines[line.id] &&
                                errors.lines[line.id].referenceYearDeviation,
                            })}
                          >
                            <SimpleTooltip
                              id={`ReferenceYearTooltip-${line.id}`}
                              tooltip={
                                line.registrationsUntil
                                  ? intl.formatMessage(messages.FiscalYearCustomerOn, {
                                      date: dateFormatService.formatDayMonth(
                                        moment
                                          .utc(line.registrationsUntil)
                                          .add(line.referenceYearDeviation ?? 0, 'days'),
                                      ),
                                    })
                                  : null
                              }
                            >
                              <span>
                                <EditableNumber
                                  min="-366"
                                  max="366"
                                  allowDecimals={false}
                                  value={line.referenceYearDeviation}
                                  onChange={year =>
                                    this.updateLine(line, {
                                      referenceYearDeviation: year === '' ? 0 : parseInt(year),
                                    })
                                  }
                                  displayValue={getDeviationDays(
                                    line.referenceYearDeviation,
                                    intl,
                                    true,
                                  )}
                                />
                              </span>
                            </SimpleTooltip>
                          </td>
                        ) : null}
                        <td className="text-right col-icons">
                          {line.isRemoved ? (
                            <a onClick={() => this.updateLine(line, { isRemoved: false })}>
                              <FaUndo />
                            </a>
                          ) : (
                            <a onClick={() => this.removeLine(line)}>
                              <FaTrashAlt />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                    {showValidation && data.lines && !data.lines.length && (
                      <tr>
                        <td colSpan="4" className="text-danger">
                          <FormattedMessage {...messages.NoScheduleLines} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
              {taskflowLines.length > 0 && (
                <table className="table table-hover table-layout">
                  <thead>
                    <tr>
                      <th className="text-center">
                        <FormattedMessage {...messages.IsTaskflowBilling} />
                        <ZendeskHelp article="19193674748188" />
                      </th>
                      <th>
                        <FormattedMessage {...messages.MainTask} />
                      </th>
                      <th>
                        <FormattedMessage {...messages.Subtask} />
                      </th>

                      {data.invoicingType === 2 && (
                        <th>
                          <FormattedMessage {...messages.AdvanceOrSettlement} />
                        </th>
                      )}
                      <th>
                        <FormattedMessage {...messages.InvoiceText} />
                      </th>
                      {this.hasReferenceYear && data.useReferenceYear ? (
                        <th>
                          <FormattedMessage {...messages.ReferenceYear} />
                        </th>
                      ) : null}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {taskflowLines.map(line => {
                      const subTasks = this.getSubTasks(line.mainTaskTemplateId);
                      return (
                        <tr
                          key={line.id}
                          className={classnames({ 'mark-removed': line.isRemoved })}
                        >
                          <td className="text-center col-icons">
                            <Checkbox
                              value={true}
                              onClick={() =>
                                this.updateLine(line, {
                                  mainTaskTemplateId: null,
                                  subtaskTemplateId: null,
                                })
                              }
                              disabled={saving}
                            />
                          </td>
                          <td className="col-md">
                            <select
                              name="mainTaskTemplateId"
                              value={
                                !line.mainTaskTemplateId || line.mainTaskTemplateId === guid.empty
                                  ? ''
                                  : line.mainTaskTemplateId
                              }
                              onChange={e =>
                                this.updateLine(line, {
                                  mainTaskTemplateId: e.target.value || guid.empty,
                                  subtaskTemplateId: null,
                                })
                              }
                              disabled={saving}
                              className={classnames('form-control', {
                                markedError:
                                  showValidation && errors.lines[line.id]?.mainTaskTemplateId,
                              })}
                              required
                            >
                              {!line.mainTaskTemplateId ||
                              line.mainTaskTemplateId === guid.empty ? (
                                <option value=""></option>
                              ) : null}
                              {taskTemplates.map(t => (
                                <option key={t.id} value={t.id}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="col-md">
                            {!!line.mainTaskTemplateId && line.mainTaskTemplateId !== guid.empty && (
                              <select
                                name="subtaskTemplateId"
                                value={
                                  !line.subtaskTemplateId || line.subtaskTemplateId === guid.empty
                                    ? ''
                                    : line.subtaskTemplateId
                                }
                                onChange={e =>
                                  this.updateLine(line, {
                                    subtaskTemplateId: e.target.value || guid.empty,
                                  })
                                }
                                className={classnames('form-control', {
                                  markedError:
                                    showValidation && errors.lines[line.id]?.subtaskTemplateId,
                                })}
                                disabled={saving}
                                required
                              >
                                {!line.subtaskTemplateId ||
                                line.subtaskTemplateId === guid.empty ? (
                                  <option value=""></option>
                                ) : null}
                                {subTasks.map(t => (
                                  <option key={t.id} value={t.id}>
                                    {t.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          {data.invoicingType === 2 && (
                            <td className="col-sm">
                              {line.fixed ? (
                                <FormattedMessage {...messages.Advance} />
                              ) : (
                                <FormattedMessage {...messages.Settlement} />
                              )}
                            </td>
                          )}

                          <td className="col-md">
                            <EditableSelect
                              value={line.invoiceTextId}
                              onChange={invoiceTextId => this.updateLine(line, { invoiceTextId })}
                              options={
                                invoiceTextOptions[
                                  getInvoiceTextType(data.invoicingType, line.fixed)
                                ] || []
                              }
                              bindToId
                              displayValue={getDisplayValueForInvoiceText(
                                line,
                                data,
                                invoiceTexts,
                                intl,
                              )}
                            />
                          </td>
                          {this.hasReferenceYear && data.useReferenceYear ? (
                            <td className="col-md">
                              <FormattedMessage {...messages.UseReferenceYearOfTask} />
                            </td>
                          ) : null}
                          <td className="text-right col-icons">
                            {line.isRemoved ? (
                              <a onClick={() => this.updateLine(line, { isRemoved: false })}>
                                <FaUndo />
                              </a>
                            ) : (
                              <a onClick={() => this.removeLine(line)}>
                                <FaTrashAlt />
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {showValidation && data.lines && !data.lines.length && (
                      <tr>
                        <td colSpan="4" className="text-danger">
                          <FormattedMessage {...messages.NoScheduleLines} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {data && data.invoicingType === 0 && (
            <Button
              btnClassName="pull-left btn-inverse"
              disabled={saving}
              onClick={() => this.addLine({ fixed: false })}
            >
              <FormattedMessage {...messages.AddLine} />
            </Button>
          )}
          {data && data.invoicingType === 1 && (
            <Button
              btnClassName="pull-left btn-inverse"
              disabled={saving}
              onClick={() => this.addLine({ fixed: true })}
            >
              <FormattedMessage {...messages.AddLine} />
            </Button>
          )}
          {data && data.invoicingType === 2 && (
            <span className="pull-left">
              <Button
                disabled={saving}
                onClick={() => this.addLine({ fixed: true })}
                btnClassName="btn-inverse"
              >
                <FormattedMessage {...messages.AddAdvanceLine} />
              </Button>
              <Button
                disabled={saving}
                onClick={() => this.addLine({ fixed: false })}
                btnClassName="btn-inverse"
              >
                <FormattedMessage {...messages.AddSettlementLine} />
              </Button>
            </span>
          )}

          <Button disabled={saving} onClick={() => onClose(null)}>
            {intl.formatMessage(messages.Cancel)}
          </Button>
          <Button
            onClick={() => this.save()}
            disabled={showValidation && !errors.isValid}
            onFocus={this.showValidation}
            onMouseOver={this.showValidation}
            btnClassName="btn-inverse"
            showProgress
          >
            {intl.formatMessage(messages.Save)}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

InvoiceScheduleModal.propTypes = {
  schedule: nullable(PropTypes.shape({})).isRequired,
  onClose: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  invoiceTexts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default injectIntl(InvoiceScheduleModal);
